import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject, ChangeDetectorRef, ViewChild, ElementRef, computed, signal, input, effect, untracked, DestroyRef, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Room, RoomEvent, Track, LocalAudioTrack, RemoteParticipant, RemoteTrack, RemoteTrackPublication, ParticipantKind, TranscriptionSegment, Participant } from 'livekit-client';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SliderModule } from 'primeng/slider';
import { AccordionModule } from 'primeng/accordion';
import { ToastModule } from 'primeng/toast';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { Popover, PopoverModule } from 'primeng/popover';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '@core/services/language.service';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, tap, takeUntil } from 'rxjs/operators';
import { BEARER_TOKEN_OVERRIDE } from '@core/interceptors/credentials.interceptor';

import { AgentsService } from '@generated/services/agents.service';
import { SessionsService } from '@generated/services/sessions.service';
import { LanguagesService } from '@generated/services/languages.service';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { Signalr } from '@core/services/signalr';
import { DispatchAgentResult } from '@generated/models/dispatch-agent-result';
import { AgentResult } from '@generated/models/agent-result';
import { GetLanguageLocalesResult } from '@generated/models/get-language-locales-result';
import { GetPublicAgentResult } from '@generated/models/get-public-agent-result';
import { PublicAgentDto } from '@generated/models/public-agent-dto';
import { PublishOptionsDto } from '@generated/models/publish-options-dto';
import { OpenApiPropertiesSchema } from '@generated/models/open-api-properties-schema';
import { RoomMetadata } from '@generated/models/room-metadata';
import { ChatMessageDto } from '@generated/models/chat-message-dto';
import { ChatRole } from '@generated/models/chat-role';
import { ChatHistoryViewComponent } from '@shared/components/chat-history-view/chat-history-view.component';
import { PropertiesEdit } from '@shared/components/properties-edit/properties-edit';
import { descriptorEntriesToDict, dictToValueEntries } from '@shared/utils/openapi-schema-utils';
import { environment } from '@env/environment';
import { ThemeService } from '@core/services/theme.service';

type SessionStatus = 'none' | 'waiting' | 'connected' | 'disconnected';

/**
 * AgentTestComponent — ЕДИНСТВЕННЫЙ компонент виджета/тестера агента в
 * этом монорепо.
 *
 * НЕ ПОВТОРЯЙ мою ошибку и НЕ ищи виджет в других репозиториях —
 * ../angular-widget это старая заброшенная копия. Правки UI виджета
 * делаются ТОЛЬКО здесь.
 *
 * ============================ ЧЕТЫРЕ РЕЖИМА ============================
 *
 * Имена складываются в матрицу 2×2: {виджет | страница} × {превью |
 * публикация}.
 *
 *   ПРЕВЬЮ    — мы НА ПЛАТФОРМЕ: внутри админки, вокруг шелл, есть тема
 *               пользователя, агента смотрит его владелец.
 *   ПУБЛИКАЦИЯ — мы ВНЕ платформы: агента открывает конечный
 *               пользователь, наследовать вокруг нечего.
 *
 *   ВИДЖЕТ    — компактная карточка со своим позиционированием.
 *   СТРАНИЦА  — полноразмерный интерфейс в потоке страницы.
 *
 *   1. ВИДЖЕТ-ПРЕВЬЮ — секция «Виджет» в agent-edit.
 *        <app-agent-test [widgetMode]="true" [inheritPlatformTheme]="true"
 *                        [publicAgentOverride]="…">
 *        isWidgetMode() = true, isPublicMode() = false.
 *        Показывает, как виджет будет выглядеть у клиента, поэтому
 *        publish_options применяются ЖИВЫМИ, без сохранения (их приносит
 *        publicAgentOverride). Тема — исключение: берётся от платформы,
 *        иначе светлый виджет висел бы пятном в тёмной админке.
 *
 *   2. ВИДЖЕТ-ПУБЛИКАЦИЯ — виджет у клиента на сайте.
 *        <speaknode-agent agent-id="…"> + loader-скрипт.
 *        Собирается ОТДЕЛЬНЫМ бандлом: projects/widget/web-component.ts
 *        оборачивает этот класс через createCustomElement.
 *        isWidgetMode() = true, isPublicMode() = false.
 *        Всё из настроек публикации, включая тему: страница-хост чужая,
 *        наследовать там нечего.
 *
 *   3. СТРАНИЦА-ПРЕВЬЮ — тестирование агента владельцем.
 *        Роут /agents/:id/test и вкладка «Talk to agent» в agent-edit.
 *        isWidgetMode() = false, isPublicMode() = false.
 *        Полноразмерный интерфейс: никакого позиционирования и compact-
 *        вариантов, тема — от платформы.
 *
 *   4. СТРАНИЦА-ПУБЛИКАЦИЯ — формально часть приложения, но НЕ платформа.
 *        Роут /published/agents/:id: открывается по прямой ссылке либо
 *        встраивается клиентом в iframe. Юзер не залогинен, шелла и
 *        пользовательской темы вокруг нет.
 *        isWidgetMode() = false, isPublicMode() = true.
 *        Полноразмерный интерфейс, но тема — из настроек публикации.
 *
 * ===================== ЧТО ОТКУДА В КАЖДОМ РЕЖИМЕ =====================
 *
 *   ✔ — берётся из publish_options. Иначе в клетке то, что применяется
 *   вместо настройки.
 *
 *                        1 виджет-     2 виджет-    3 страница-   4 страница-
 *   свойство                   превью      публикация      превью      публикация
 *   -------------------------------------------------------------------------------
 *   theme                    платформы         ✔         платформы         ✔
 *   appearance                   ✔             ✔            Full          Full
 *   placement                    ✔             ✔          в потоке      в потоке
 *   show_transcript              ✔             ✔           виден         виден
 *   allow_variables_input        ✔             ✔         спрашиваем        ✔
 *   language_dropdown            ✔             ✔             ✔             ✔
 *   main_label                   ✔             ✔        agent.title   agent.title
 *   button_label                 ✔             ✔             ✔             ✔
 *   end_call_button_label        ✔             ✔             ✔             ✔
 *   «Powered by»                 ✔             ✔            нет           нет
 *
 * Почему дефолты именно такие:
 *   appearance → Full, placement → в потоке, «Powered by» → нет
 *     Это layout ВИДЖЕТА. В страница-режимах карточка полноразмерная и
 *     стоит в потоке страницы, ей нечего позиционировать; положение
 *     iframe'а на чужом сайте задаёт сам сайт, а не мы.
 *   show_transcript → транскрипт виден
 *     Настройка про то, прятать ли ленту реплик в компактной карточке
 *     виджета. На полноразмерной странице транскрипт — часть нормального
 *     UI, прятать его по настройке публикации незачем.
 *   allow_variables_input → спрашиваем всегда (только страница-превью)
 *     Тумблер про КОНЕЧНОГО пользователя, поэтому работает в обеих
 *     публикациях И в виджет-превью — превью обязано показывать то, что
 *     увидит клиент. Игнорируем его ровно в одном месте: страница-превью,
 *     то есть когда владелец тестирует агента. Там переменные нужны
 *     всегда, иначе звонок уйдёт с пустыми значениями и проверить агента
 *     нечем.
 *   main_label → agent.title
 *     Это заголовок карточки виджета. В страница-режимах заголовок берём
 *     из самого агента.
 *     ВНИМАНИЕ: в страница-публикации `agent` не загружается (данные
 *     приходят в `publicAgent`), поэтому фактически показывается дефолт
 *     «Agent». Похоже на баг, но менять поведение без решения не стал.
 *
 * Гейты в коде: isWidgetMode() отвечает ТОЛЬКО за виджетный layout,
 * isPublicMode() — за «мы вне платформы». Остальные publish_options
 * доступны во всех режимах через effectivePublishOptions().
 */
@Component({
  selector: 'app-agent-test',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    ProgressSpinnerModule,
    SliderModule,
    AccordionModule,
    ToastModule,
    ChipModule,
    DividerModule,
    InputTextModule,
    SelectModule,
    MenuModule,
    PopoverModule,
    ToggleSwitchModule,
    TagModule,
    TooltipModule,
    ChatHistoryViewComponent,
    PropertiesEdit,
    RouterLink,
  ],
  templateUrl: './agent-test.html',
  styleUrl: './agent-test.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Дублируем appearance-класс на HOST-элемент (<speaknode-agent>
    // в widget-режиме или <app-agent-test> в обычном). Нужно для
    // :host(.appearance-Tiny) селекторов в CSS — там мы делаем host
    // inline-block + fit-content, иначе он растягивает всё внутри.
    '[class.appearance-Tiny]': `isTinyAppearance()`,
    '[class.appearance-Compact]': `isCompactAppearance()`,
    '[class.appearance-Full]': `isFullAppearance()`,
  }
})
export class AgentTestComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private agentsService = inject(AgentsService);
  private sessionsService = inject(SessionsService);
  private languagesService = inject(LanguagesService);
  private analyticsService = inject(AnalyticsService);
  private signalrService = inject(Signalr);
  languageService = inject(LanguageService);
  private cdr = inject(ChangeDetectorRef);

  // Observer mode is engaged when the page is opened with a `roomToken`
  // query param — we then skip the dispatch flow and connect directly to
  // the LiveKit room as a silent listener. No mic, controls hidden.
  readonly isObserver = signal<boolean>(false);
  private observerRoomToken: string | null = null;

  // Speaker on/off (mutes/unmutes attached remote audio elements).
  // Default ON for normal mode, OFF for observer mode.
  readonly speakerOn = signal<boolean>(true);
  private attachedAudioEls: HTMLMediaElement[] = [];

  // ─── Widget inputs ───────────────────────────────────────────────────
  // Signal-based inputs (Angular 17.1+). Все параметры можно менять на лету
  // как через property setters (TypeScript), так и через HTML-атрибуты
  // Web Component'а (@angular/elements мапит camelCase → kebab-case).
  // Внутри читаем как функции: this.agentId(), this.publishOptions() и т.п.
  // computed'ы и effect'ы автоматически реагируют на изменения.

  /** Опциональный override agent-id. Если передан, перекрывает значение
   *  из route (для widget'а где route недоступен). */
  readonly agentIdInput = input<string | null>(null, { alias: 'agentId' });
  /** Готовый JWT агента — для widget mode'а на чужом домене, чтобы не
   *  ходить за токеном через нашу авторизацию. */
  readonly agentTokenInput = input<string | null>(null, { alias: 'agentToken' });
  /** Каллер сам передаёт publish_options (appearance, placement,
   *  show_transcript, button_label, …) — перекрывает любые загруженные. */
  readonly publishOptionsInput = input<PublishOptionsDto | null>(null, { alias: 'publishOptions' });
  /** Полный PublicAgentDto — позволяет вообще не дёргать /public-эндпойнт,
   *  каллер передаёт languages + publish_options скопом. */
  readonly publicAgentOverride = input<PublicAgentDto | null>(null);

  // ─── Эффективные значения (input ?? fallback) ─────────────────────────
  /** agent-id из route (для обычной авторизованной страницы). */
  private readonly routeAgentId = signal<string | null>(null);
  /** Текущее значение agent-id — input wins, иначе из route. */
  readonly agentId = computed(() => this.agentIdInput() ?? this.routeAgentId());

  /** agent-token полученный по API (loadAgent/loadPublicAgent). */
  private readonly fetchedAgentToken = signal<string | null>(null);
  /** Текущий agent-token — input wins, иначе fetched. */
  readonly agentToken = computed(() => this.agentTokenInput() ?? this.fetchedAgentToken());

  /**
   * Явный флаг widget-режима. Каллер (web-component bootstrap, либо
   * обёртка для /published/agents/:id) выставляет [widgetMode]="true".
   * Эвристики типа "если передан token — значит виджет" были бы хрупкими:
   * любой случайный input ломал бы layout. Лучше один прямой источник
   * правды.
   */
  readonly widgetMode = input<boolean>(true);
  /**
   * Показывать ли переключалку языка ИНТЕРФЕЙСА тестера (RU/EN — флажок
   * рядом с Call). По дефолту true: public-страница и widget-embed её
   * используют, чтобы конечный юзер видел UI на своём языке.
   * false выставляет agent-edit'овская вкладка Talk to agent: язык
   * админки уже управляется глобально, второй selector внутри тестера
   * лишний.
   */
  readonly showTranslatePicker = input<boolean>(true);
  /** Тема, явно переданная caller'ом — атрибут `theme` на
   *  `<speaknode-agent>`: владелец сайта может прибить тему виджета, не
   *  трогая publish_options. Приоритет над publish_options, но работает
   *  ТОЛЬКО в embed/public режиме (см. effectiveTheme). */
  readonly theme = input<'Light' | 'Dark' | 'Auto' | undefined>(undefined);

  /** Взять тему от платформы, а не из publish_options. Ставит превью в
   *  agent-edit — единственный виджет-режим внутри админки. */
  readonly inheritPlatformTheme = input<boolean>(false);

  /** Реактивный prefers-color-scheme:dark для theme=Auto. */
  private readonly systemPrefersDark = signal<boolean>(
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true,
  );

  /**
   * Итоговая тема виджета — ИЛИ `null`, что значит «темой распоряжается
   * платформа, не лезь».
   *
   * ============ ОТКУДА БЕРЁТСЯ ТЕМА ============
   *
   * Правило одно: ВИДЖЕТ ПРИ ЗАГРУЗКЕ ОПРЕДЕЛЯЕТ ТЕМУ САМ, А НА
   * ПЛАТФОРМЕ ОНА НАСЛЕДУЕТСЯ ОТ НАСТРОЕК ПОЛЬЗОВАТЕЛЯ.
   *
   *   виджет вне платформы (`<speaknode-agent>` на чужом сайте)
   *     — `publish_options.theme`, либо атрибут `theme`, которым
   *       владелец сайта её перебивает. Страница-хост чужая,
   *       наследовать там нечего.
   *
   *   публичная страница `/published/agents/:id` (в т.ч. в iframe)
   *     — тоже `publish_options.theme`: снаружи платформы.
   *
   *   всё, что открыто ВНУТРИ платформы — `/agents/:id/test`, вкладка
   *   Talk to agent и превью виджета в agent-edit
   *     — `null`. Тему держит ThemeService классом `.p-dark` на <html>
   *       (выбор пользователя: light / dark / системная), компонент её
   *       просто наследует, как любой другой экран админки.
   *
   * Превью в agent-edit — единственный случай, когда виджет-режим
   * работает ВНУТРИ платформы, поэтому оно и просит `inheritPlatformTheme`:
   * остальные publish_options (appearance, placement, заголовок, тексты
   * кнопок) оно показывает как есть, а тему берёт от админки — иначе
   * светлый виджет висел бы пятном в тёмной админке.
   *
   * `loadedPublishOptions` читаем напрямую (а не через
   * `effectivePublishOptions`) — так было исторически, когда последний
   * занулялся вне widget-mode.
   */
  readonly effectiveTheme = computed<'Light' | 'Dark' | 'Auto' | null>(() => {
    if (this.inheritPlatformTheme()) return null;
    if (this.isPublicMode() || this.isWidgetMode()) {
      return this.theme() ?? this.loadedPublishOptions()?.theme ?? 'Light';
    }
    return null;
  });

  /** Класс для .tg-card — `p-dark` включает dark preset PrimeNG.
   *  Dark → всегда; Auto → только когда ОС в dark; `null` (режим
   *  платформы) → пусто, темой распоряжается `.p-dark` на <html>. */
  readonly cardThemeClass = computed<string>(() => {
    const t = this.effectiveTheme();
    if (t === 'Dark') return 'p-dark';
    if (t === 'Auto' && this.systemPrefersDark()) return 'p-dark';
    return '';
  });

  /** CSS-класс для overlay'ев (popover / menu). widget-popover — z-index
   *  над frame'ом; +p-dark активирует dark preset PrimeNG для overlay,
   *  который монтируется в body (иначе не унаследует .tg-card). */
  readonly popoverStyleClass = computed(() => {
    const dark = this.cardThemeClass() === 'p-dark';
    return dark ? 'widget-popover p-dark' : 'widget-popover';
  });

  agent: AgentResult | null = null;
  publicAgent: GetPublicAgentResult | null = null;
  /** Маркер «мы на /published/agents/:id». Реактивный — computed'ы
   *  isWidgetMode / effectivePublishOptions реагируют на смену. */
  readonly isPublicMode = signal<boolean>(false);

  /**
   * Widget mode = embed-режим, в котором уважаем publish_options.
   * Включается явно через input widgetMode или автоматически на
   * /published/agents/:id роуте. В обычном /agents/:id/test всегда
   * false → дефолтный Full layout.
   */
  /** Layout-режим виджета: Tiny/Compact/placement/Powered by и т.д.
   *  Только когда каллер (custom-element bootstrap) явно передал
   *  [widgetMode]="true". Публичная страница /published/agents/:id — НЕ
   *  widget'овая: там мы показываем полноценный full-page интерфейс как
   *  в /agents/:id/test (юзер попадает по прямой ссылке и ожидает
   *  нормальный UI, а не floating-кнопку в углу). */
  /**
   * Дефолт widgetMode=true — под кейс когда компонент маунтится как
   * custom-element `<speaknode-agent>`: там caller (webсайт) не указывает
   * никаких атрибутов, а нам всё равно нужно вести себя как виджет.
   * Route-based страницы (/agents/:id/test, /published/agents/:id) явно
   * помечены `data: { routeBased: true }` в app.routes — они full-layout,
   * не embed. Это перекрывает input. agent-edit'овская вкладка Talk to
   * agent (встроенный <app-agent-test>) передаёт [widgetMode]="false".
   */
  readonly isWidgetMode = computed(() => {
    if (this.route.snapshot.data?.['routeBased']) return false;
    return this.widgetMode();
  });

  /** Auth-режим по agent_token'у (без Keycloak). Используется для гейтов
   *  вокруг Bearer'а и /public endpoint'а: и widget-mode, и публичная
   *  страница ходят на бэк без юзерской сессии, а с agent_token'ом.
   *
   *  isWidgetMode(), а НЕ сырой widgetMode(): у входа дефолт true (под
   *  custom-element, где атрибутов не передают), и route-based страницы
   *  перекрывают его через data.routeBased. Раньше здесь стоял сырой
   *  вход, и на /agents/:id/test — странице за авторизацией — данные
   *  агента тянулись публичным endpoint'ом. У опубликованного агента это
   *  случайно работало, у неопубликованного страница падала в «Unable to
   *  load agent data», хотя права у пользователя есть. Ровно так
   *  ломалось прослушивание прогона: ссылка наблюдателя ведёт сюда же. */
  readonly useAgentTokenAuth = computed(() => this.isWidgetMode() || this.isPublicMode());

  /** Publish-options, прилетевшие из loadPublicAgent. Раздельный signal
   *  чтобы computed'ы могли подписаться без мирроринга всего DTO. */
  private readonly loadedPublishOptions = signal<PublishOptionsDto | null>(null);

  /** variables_open_api_schema агента. Прилетает либо из admin `loadAgent`
   *  (this.agent), либо из public `loadPublicAgent` (this.publicAgent).
   *  Отдельный signal — чтобы computed для user-fillable dict реагировал
   *  на изменения без reference'а на plain-object'ах. */
  private readonly variablesSchemaSignal = signal<OpenApiPropertiesSchema | null>(null);

  /** Отфильтрованный от system__* dict для <app-properties-edit>.
   *  Возвращает null, если не осталось редактируемых переменных. */
  readonly variableSchemaDict = computed<Record<string, any> | null>(() => {
    const schema = this.variablesSchemaSignal();
    if (!schema?.properties?.length) return null;
    const dict = descriptorEntriesToDict(schema.properties);
    const filtered: Record<string, any> = {};
    for (const [k, v] of Object.entries(dict)) {
      if (!k.startsWith('system__')) filtered[k] = v;
    }
    return Object.keys(filtered).length ? filtered : null;
  });

  /** Заполненные пользователем значения — уйдут в dispatch как `variables`. */
  readonly variableValues = signal<Record<string, any>>({});

  /** Валидность формы переменных — эмитится PropertiesEdit'ом. Дефолт true:
   *  если формы нет вообще — не блокируем старт звонка. */
  readonly variablesValid = signal<boolean>(true);

  /** required-ключи схемы (без system__*) — прокидываем в PropertiesEdit,
   *  он вычислит валидность (плюс сам добавит non-nullable). */
  readonly variableRequiredKeys = computed<string[]>(() => {
    const schema = this.variablesSchemaSignal();
    const list = schema?.required ?? [];
    return list.filter(k => !k.startsWith('system__'));
  });

  /**
   * true = агенту нужны переменные перед стартом звонка.
   *
   * `allow_variables_input` уважаем везде, где агент открыт КОНЕЧНЫМ
   * пользователем — и в виджете на чужом сайте, и на публичной странице
   * `/published/agents/:id`: форму ввода он получать не должен, если
   * владелец агента её не разрешил.
   *
   * А вот на платформе (тестер `/agents/:id/test`, вкладка «Поговорить»)
   * тумблер игнорируем и спрашиваем всегда, если у агента есть
   * non-system переменные: иначе звонок уйдёт с пустыми значениями и
   * проверить агента нечем.
   */
  get needsVariablesInput(): boolean {
    if (!this.variableSchemaDict()) return false;
    if (!this.isWidgetMode() && !this.isPublicMode()) return true;
    const opts = this.effectivePublishOptions()
      ?? this.publicAgent?.public_agent?.publish_options
      ?? this.agent?.public_agent?.publish_options
      ?? null;
    return opts?.allow_variables_input === true;
  }

  /** Открыта ли inline-форма ввода переменных в теле виджета. Юзер жмёт
   *  Start → если нужны переменные, показываем форму (без старта); юзер
   *  жмёт Submit под формой → закрываем форму и стартуем реальный звонок. */
  readonly variablesFormOpen = signal<boolean>(false);

  onVariableValuesChange(values: Record<string, any>): void {
    this.variableValues.set(values);
  }

  /** Схему меняем строго через сеттер — computed variableSchemaDict
   *  и showVariablesInput перечитаются, форма перерисуется. */
  private setVariablesSchema(schema: OpenApiPropertiesSchema | null | undefined): void {
    this.variablesSchemaSignal.set(schema ?? null);
  }

  /** Собираем entries для DispatchAgentRequest.variables. Только те ключи,
   *  что есть в текущей (отфильтрованной от system__*) схеме — юзер мог
   *  ввести значение, потом схема изменилась, старые ключи не отправляем. */
  private variablesForDispatch(): Array<{ name: string; value: any }> | null {
    const schemaDict = this.variableSchemaDict();
    if (!schemaDict) return null;
    const values = this.variableValues();
    const filtered: Record<string, any> = {};
    for (const [k, v] of Object.entries(values)) {
      if (schemaDict[k] !== undefined) filtered[k] = v;
    }
    if (Object.keys(filtered).length === 0) return null;
    return dictToValueEntries(filtered);
  }

  /**
   * Эффективные publish_options. Приоритет:
   *   input publishOptions > publicAgentOverride.publish_options
   *   > loadedPublishOptions (из API).
   * Доступны во ВСЕХ режимах: заголовок, тексты кнопок, show_transcript
   * и прочее применяются и на публичной странице, и в тестере платформы.
   * Исключение — appearance и placement: это layout виджета, он
   * включается только в widget-режиме (см. appearanceClass /
   * placementClass и isTiny/isCompact/isFullAppearance).
   */
  readonly effectivePublishOptions = computed<PublishOptionsDto | null>(() => {
    // publicAgentOverride().publish_options НЕ читаем напрямую — там
    // тот же reference на живой объект в родительском компоненте,
    // signal-цепочка по === не увидит изменения .placement и т.п.
    // Effect ниже кладёт свежую копию в loadedPublishOptions, оттуда
    // и берём.
    return this.publishOptionsInput()
      ?? this.loadedPublishOptions()
      ?? null;
  });

  /**
   * Транскрипт. `show_transcript` — настройка ИМЕННО виджета: она про то,
   * показывать ли ленту реплик в компактной карточке на чужом сайте.
   * Вне widget-режима (тестер платформы, «Поговорить», публичная
   * страница) транскрипт есть всегда — там он часть нормального UI, и
   * прятать его по настройке публикации незачем.
   */
  readonly transcriptVisible = computed(() => {
    if (!this.isWidgetMode()) return true;
    return this.effectivePublishOptions()?.show_transcript !== false;
  });

  /**
   * CSS-класс placement'а для карточки. Вне widget mode — пустой,
   * т.к. классическая /agents/:id/test страница не позиционируется
   * фиксированно. В preview'e agent-edit класс наследуется, но
   * .widget-preview-container перекрывает position: static.
   */
  readonly placementClass = computed<string>(() => {
    if (!this.isWidgetMode()) return '';
    const p = this.effectivePublishOptions()?.placement;
    return p ? `placement-${p}` : '';
  });

  /** CSS-класс appearance'а. Дефолт Full. */
  readonly appearanceClass = computed<string>(() => {
    if (!this.isWidgetMode()) return 'appearance-Full';
    const a = this.effectivePublishOptions()?.appearance ?? 'Full';
    return `appearance-${a}`;
  });

  /**
   * Label для Start-кнопки. В Full-варианте виджета рядом с иконкой
   * трубки пишем текст: publish_options.button_label либо дефолт
   * "Start call". Для Compact/Tiny и вне widget-mode — null (только
   * иконка). null → template подставит [rounded]=true.
   */
  readonly startButtonLabel = computed<string | null>(() => {
    const opts = this.effectivePublishOptions();
    const appearance = this.isWidgetMode() ? (opts?.appearance ?? 'Full') : 'Full';
    if (appearance !== 'Full') return null;
    return opts?.button_label || $localize`:@@agentTest.startCall:Start call`;
  });

  /** Label для End-кнопки — та же логика, но end_call_button_label. */
  readonly endButtonLabel = computed<string | null>(() => {
    const opts = this.effectivePublishOptions();
    const appearance = this.isWidgetMode() ? (opts?.appearance ?? 'Full') : 'Full';
    if (appearance !== 'Full') return null;
    return opts?.end_call_button_label || $localize`:@@agentTest.endCall:End call`;
  });

  /**
   * Тултип для Start-кнопки. В отличие от startButtonLabel НЕ гейтится
   * по appearance — в Compact/Tiny рядом с иконкой лейбла нет, и юзер
   * должен видеть значение при hover'e. Дефолтный тултип — "Start" вне
   * widget-mode.
   */
  readonly startButtonTooltip = computed<string>(() => {
    // Если открыта форма переменных — Start дисейбл'ен, объясняем что
    // делать (жать Submit под формой).
    if (this.variablesFormOpen()) {
      return $localize`:@@agentTest.variables.fillAndSubmitTooltip:Fill in the variables below and press Submit to start the call.`;
    }
    return this.effectivePublishOptions()?.button_label
      || $localize`:@@agentTest.startCall:Start call`;
  });

  /** Тултип End-кнопки — та же логика. */
  readonly endButtonTooltip = computed<string>(() => {
    return this.effectivePublishOptions()?.end_call_button_label
      || $localize`:@@agentTest.endCall:End call`;
  });

  /**
   * В виджете статус показываем цветной точкой (весь текст в tooltip'e) —
   * во ВСЕХ appearance'ах, включая Full: p-tag "Agent: Ready" рядом с
   * name смотрится тяжеловесно. Вне widget-mode (internal /agents/:id/test)
   * оставляем полноценный p-tag — там UI информативнее.
   */
  readonly isCompactStatus = computed<boolean>(() => {
    return this.isWidgetMode();
  });

  /** Tiny — самый ужатый режим: скрываем и имя, и статус-точку,
   *  оставляем только аватар агента с tooltip'ом (имя + статус). */
  readonly isTinyAppearance = computed<boolean>(() => {
    if (!this.isWidgetMode()) return false;
    return this.effectivePublishOptions()?.appearance === 'Tiny';
  });

  /** Compact — имя+точка в одну строку, без body. */
  readonly isCompactAppearance = computed<boolean>(() => {
    if (!this.isWidgetMode()) return false;
    return this.effectivePublishOptions()?.appearance === 'Compact';
  });

  /** Full — дефолт: p-tag со статусом под именем, полноразмерная карточка.
   *  Вне widget-mode тоже Full — там publish_options не применяем. */
  readonly isFullAppearance = computed<boolean>(() => {
    if (!this.isWidgetMode()) return true;
    const a = this.effectivePublishOptions()?.appearance;
    return !a || a === 'Full';
  });

  /** Tooltip аватара в Tiny — "Agent name • Agent: Ready". */
  get avatarTooltip(): string {
    return `${this.agentName} • ${this.statusText}`;
  }

  /** "Agent: Ready" / "Agent: Connected" / … — с префиксом для контекста. */
  get statusText(): string {
    const prefix = $localize`:@@agentTest.statusPrefix:Agent`;
    return `${prefix}: ${this.getStatusText()}`;
  }
  isLoadingAgent = false;
  loadAgentError = false;
  languagesData: GetLanguageLocalesResult | null = null;
  room: Room | null = null;
  localAudioTrack: LocalAudioTrack | null = null;
  private originalMicTrack: MediaStreamTrack | null = null;

  // Analytics tracking
  private sessionStartTime: number | null = null;
  private analyticsInterval: any = null;

  sessionStatus: SessionStatus = 'none';
  roomName: string | null = null;
  sessionId: string | null = null;
  /** Session-scoped bearer token из dispatchResult — сохраняем, чтобы после
   *  disconnect'а построить публичную ссылку на детали сессии
   *  (/published/agents/:id/conversations/:sid?sessionToken=…). Через
   *  этот же токен conversation-detail компонент авторизуется на бэке. */
  private sessionToken: string | null = null;
  agentParticipant: RemoteParticipant | null = null;

  // Audio level & gain
  audioLevel = 0;
  micVolume = 100;
  /** 0..100 — громкость, прикладывается ко всем attached audio-элементам. */
  speakerVolume = 100;
  private audioLevelAnimationId: number | null = null;

  /**
   * Pre-session preview микрофона: пока открыт mic-popover до старта
   * сессии, держим отдельный getUserMedia-стрим + AnalyserNode и в RAF
   * считаем RMS — UI рисует уровень в реальном времени. На закрытии
   * попловера всё освобождаем.
   */
  previewLevel = 0;
  private previewStream: MediaStream | null = null;
  private previewAudioContext: AudioContext | null = null;
  private previewAnalyser: AnalyserNode | null = null;
  private previewGainNode: GainNode | null = null;
  private previewRafId: number | null = null;
  private previewBuffer: Uint8Array | null = null;
  /** Знаем что mic-popover виден — нужно для (un)mute pre-session,
   *  чтобы переподнять/потушить preview-стрим при переключении тоггла. */
  private micPopoverOpen = false;

  /**
   * Единый источник уровня входа для UI: если сессия активна — реальный
   * audioLevel из LiveKit-трека, иначе — preview из getUserMedia.
   * Используется в p-progressBar внутри mic-popover.
   */
  get micLevel(): number {
    return this.isConnected ? this.audioLevel : this.previewLevel;
  }
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;

  isMuted = false;
  isLoading = false;
  pendingRoomToken: string | null = null;

  /**
   * Состояние браузерного разрешения на микрофон. 'unknown' — Permissions
   * API не отвечает (Safari, не-secure-context); тогда состояние
   * выясним при первом getUserMedia. Остальное — из PermissionStatus,
   * обновляется live через .onchange.
   */
  micAccess: 'unknown' | 'granted' | 'denied' | 'prompt' = 'unknown';
  /**
   * Inline-сообщение, рендерится прямо в .tg-body вместо тостов.
   * Errors висят пока контекст не сменится (например, новое Start
   * заглушает предыдущий connectFail); warn/info/success — авто-уход
   * через inlineMessageTimer.
   */
  inlineMessage: {
    type: 'error' | 'warn' | 'info' | 'success';
    text: string;
    /** Опциональная кликабельная ссылка справа от текста. Может быть
     *  или router-link (`commands` для навигации внутри app) или
     *  внешний href (`href` — открываем target=_blank; используется
     *  для widget-embed'а, чтобы уйти на наш app, а не на origin
     *  сайта-хостера). Взаимоисключающие поля. */
    link?: {
      text: string;
      commands?: any[];
      queryParams?: Record<string, string>;
      href?: string;
    };
  } | null = null;
  private inlineMessageTimer: ReturnType<typeof setTimeout> | null = null;
  /** Текст последней не-permission ошибки микрофона (NotFound,
   *  NotReadable, Overconstrained, Security) — показываем баннером
   *  в body. Дублирует inlineMessage по семантике; держим отдельно
   *  ради читаемости applyMicError/preflightMicrophone. */
  micErrorText: string | null = null;
  /** Юзер уже жал «Запросить доступ» и получил отказ — значит браузер
   *  стоит в persist-denied и нового prompt'а уже не покажет. UI
   *  переключается на «руками открой настройки + проверить заново». */
  micRequestAttempted = false;
  /**
   * Раскрывать ли банн про mic-denied. false до тех пор пока юзер сам
   * не попытается стартовать сессию (клик Start) или явно запросил
   * доступ. Иначе виджет при загрузке сразу растопыривается в
   * expanded-состояние если разрешение уже было denied заранее —
   * лишний шум, юзер даже не пытался использовать. */
  private micErrorRevealed = false;
  private micPermissionStatus: PermissionStatus | null = null;
  private micPermissionChangeHandler: (() => void) | null = null;
  // Если юзер нажал Stop в момент connectToRoom — не даём отложенным колбэкам
  // (signalr / session-check / timeout) воскресить сессию
  private userStopRequested = false;
  // Гасит все RxJS-подписки и таймеры текущего сеанса (dispatch, session-check, 15s fallback)
  private sessionEnded$ = new Subject<void>();
  private connectFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  controlsAccordionValue: string[] = ['settings'];

  @ViewChild('chatContainer') chatContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('uiLanguageMenu') uiLanguageMenu!: Menu;

  uiLanguageMenuItems = computed<MenuItem[]>(() => {
    const current = this.languageService.currentLang();
    return this.languageService.languages.map(l => ({
      label: `${l.flag} ${l.label}`,
      icon: l.code === current ? 'pi pi-check' : undefined,
      command: () => this.languageService.setLang(l.code)
    }));
  });

  toggleUiLanguageMenu(event: Event) {
    this.uiLanguageMenu.toggle(event);
  }

  /** Guard чтобы load-effect не переспамил loadAgent()/loadPublicAgent()
   *  на каждое подергивание сигнала. */
  private _agentLoadKicked = false;

  private readonly injector = inject(Injector);

  constructor() {
    // Страница-публикация: фон ВОКРУГ карточки.
    //
    // `body` красится в `--sn-bg-ground` (src/styles.css), а cardThemeClass
    // вешает `p-dark` только на `.tg-card` — до <html> тема не доходит, и
    // вокруг тёмной карточки оставалось белое поле. Видно, когда клиент
    // встраивает страницу iframe'ом: карточка тёмная, рамка белая.
    //
    // На `/published/agents/:id` документ НАШ целиком, поэтому тему тут
    // правомерно вешать на корень — этим занимается ThemeService, он же
    // проставляет color-scheme (нативные скроллбары и автозаполнение) и
    // meta[theme-color] для мобильной строки браузера.
    //
    // ThemeService берём через injector, а не обычным inject(): в
    // widget-бандле он не должен инстанцироваться ВООБЩЕ. Там документ
    // принадлежит чужому сайту, а ThemeService в конструкторе трогает
    // <html> — снимает `.p-dark` и ставит `color-scheme: light`. Ленивый
    // get() под гейтом isPublicMode() гарантирует, что в виджете сервис
    // не создастся: этот роут там недостижим.
    effect(() => {
      if (!this.isPublicMode()) return;
      const theme = this.effectiveTheme();
      if (!theme) return;
      untracked(() => this.injector.get(ThemeService).usePublishedTheme(theme));
    });

    // 0) Реактивный prefers-color-scheme:dark для theme=Auto.
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e: MediaQueryListEvent) => this.systemPrefersDark.set(e.matches);
      mql.addEventListener('change', handler);
      inject(DestroyRef).onDestroy(() => mql.removeEventListener('change', handler));
    }

    // 1) Ловим publicAgentOverride — заполняет this.publicAgent,
    //    инициализирует languageOptions и токен.
    effect(() => {
      const override = this.publicAgentOverride();
      if (!override) return;
      this.publicAgent = { public_agent: override };
      this.fetchedAgentToken.set(override.agent_token ?? null);
      // Спред копия — иначе signal видит тот же reference и downstream
      // computed'ы (placementClass и т.п.) не пересчитаются даже если
      // поле внутри поменялось.
      this.loadedPublishOptions.set(override.publish_options ? { ...override.publish_options } : null);
      this.setVariablesSchema(override.variables_open_api_schema);
      this.initializeLanguageOptionsForPublicAgent();
      this.cdr.markForCheck();
    });

    // 2) Запускаем загрузку агента когда agentId становится truthy.
    //    В контексте Angular Elements input прилетает после ngOnInit,
    //    поэтому одноразовый чек в ngOnInit пропустил бы момент.
    //    Если каллер передал publicAgentOverride — эффект №1 закрыл
    //    все данные, load не нужен.
    effect(() => {
      if (this._agentLoadKicked) return;
      const id = this.agentId();
      if (!id) return;
      if (this.publicAgentOverride()) return;
      this._agentLoadKicked = true;
      // Public URL ИЛИ widget-mode → нет Keycloak-сессии → идём в
      // публичный endpoint. Иначе — авторизованный loadAgent.
      if (this.useAgentTokenAuth()) {
        this.loadPublicAgent();
      } else {
        this.loadAgent();
      }
    });
  }

  chatMessages: ChatMessageDto[] = [];
  /**
   * IDs сегментов, которые сейчас стримятся (partial-транскрипт). Когда
   * сегмент финализируется — id оттуда уходит, спиннер исчезает.
   * Множество, потому что у юзера и агента могут одновременно ехать
   * параллельные partial-сегменты.
   */
  streamingMessageIds: string[] = [];
  textMessage = '';

  // Language selection
  selectedLanguageLocaleId: string | null = null;
  languageOptions: { label: string; value: string; isDefault?: boolean }[] = [];

  private readonly LIVEKIT_URL = environment.livekitUrl;

  /** Название и лендинг приложения для "Powered by" футера виджета. */
  readonly appName = environment.appName;
  readonly appLandingUrl = environment.appLandingUrl;

  readonly youLabel = $localize`:@@agentTest.you:You`;
  readonly agentLabel = $localize`:@@agentTest.agent:Agent`;
  readonly micOnTooltip = $localize`:@@agentTest.mic.tooltip.on:Microphone (currently on)`;
  readonly micOffTooltip = $localize`:@@agentTest.mic.tooltip.off:Microphone (currently off)`;
  readonly speakerOnTooltip = $localize`:@@agentTest.speaker.tooltip.on:Speaker (currently on)`;
  readonly speakerOffTooltip = $localize`:@@agentTest.speaker.tooltip.off:Speaker (currently off)`;
  readonly startLabelNormal = $localize`:@@agentTest.start:Start`;
  readonly startLabelObserver = $localize`:@@agentTest.connect:Connect`;
  readonly micBlockedRequestLabel = $localize`:@@agentTest.micBlocked.request:Request access`;
  readonly micBlockedRecheckLabel = $localize`:@@agentTest.micBlocked.recheck:I've allowed it — re-check`;
  get startLabel(): string {
    return this.isObserver() ? this.startLabelObserver : this.startLabelNormal;
  }

  ngOnInit(): void {
    this.routeAgentId.set(this.route.snapshot.paramMap.get('id'));

    // Observer mode is engaged when the caller passes a room token via
    // ?roomToken=<jwt> — we skip dispatch and join the existing LiveKit
    // room as a silent listener.
    const roomToken = this.route.snapshot.queryParamMap.get('roomToken');
    if (roomToken) {
      this.isObserver.set(true);
      this.observerRoomToken = roomToken;
      this.speakerOn.set(false);
      // Снимаем взвод load-эффекта: иначе он дернёт загрузку второй раз,
      // уже после нашей.
      this._agentLoadKicked = true;
      this.loadAgent();
      this.connectAsObserver(roomToken);
      return;
    }

    this.isPublicMode.set(this.router.url.includes('/published/agents/'));

    // Загрузка данных агента запускается из agentLoadEffect() в конструкторе —
    // reactively, когда agentId() становится truthy. В контексте Angular
    // Elements входные параметры (agentId, publicAgentOverride) приходят
    // ПОСЛЕ ngOnInit через property setter'ы, поэтому синхронный чек здесь
    // рано срабатывал бы на пустом agentId. Ничего не делаем — effect сам
    // среагирует когда input подтянется.

    // Подписываемся на состояние разрешения микрофона ещё до первого
    // клика Start — чтобы юзеру не показывать кликабельный Start, если
    // он уже заранее запретил доступ.
    this.subscribeMicPermission();
  }

  private async subscribeMicPermission(): Promise<void> {
    try {
      const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
      if (!status) return;
      this.micPermissionStatus = status;
      this.micAccess = status.state as 'granted' | 'denied' | 'prompt';
      // PermissionStatus emits 'change' when user toggles разрешение
      // в адресной строке — отрабатываем сразу, без перезагрузки страницы.
      this.micPermissionChangeHandler = () => {
        this.micAccess = status.state as 'granted' | 'denied' | 'prompt';
        if (this.micAccess !== 'denied') this.micErrorText = null;
        this.cdr.markForCheck();
      };
      status.addEventListener('change', this.micPermissionChangeHandler);
      this.cdr.markForCheck();
    } catch {
      // Safari / старые браузеры не поддерживают query('microphone') —
      // оставляем micAccess='unknown', выяснится в момент getUserMedia.
    }
  }

  private unsubscribeMicPermission(): void {
    if (this.micPermissionStatus && this.micPermissionChangeHandler) {
      this.micPermissionStatus.removeEventListener('change', this.micPermissionChangeHandler);
    }
    this.micPermissionStatus = null;
    this.micPermissionChangeHandler = null;
  }

  private connectAsObserver(roomToken: string): void {
    this.sessionStatus = 'waiting';
    this.userStopRequested = false;
    this.cdr.markForCheck();

    (async () => {
      try {
        await this.connectToRoom(roomToken, '');
        this.sessionStatus = 'connected';
        this.cdr.markForCheck();
      } catch (error) {
        console.error('Observer connect failed:', error);
        this.showInline('error', $localize`:@@agentTest.notify.connectFail:Failed to connect to agent`);
        this.sessionStatus = 'disconnected';
        this.cdr.markForCheck();
      }
    })();
  }

  toggleSpeaker(): void {
    const next = !this.speakerOn();
    this.speakerOn.set(next);
    for (const el of this.attachedAudioEls) {
      el.muted = !next;
    }
  }

  /** Прикладывает текущий speakerVolume (0..100) ко всем attached audio. */
  onSpeakerVolumeChange(): void {
    const v = Math.max(0, Math.min(100, this.speakerVolume)) / 100;
    for (const el of this.attachedAudioEls) {
      el.volume = v;
    }
  }

  /**
   * Клик по кнопке «трубка» в шапке. Если есть выбор языка и языков
   * больше одного — открываем поповер со списком, ничего не стартуем.
   * Дальше выбор языка приведёт к startTest(). Иначе — стартуем сразу.
   *
   * Исключение: если микрофон уже denied — не спрашиваем язык, сессия
   * всё равно не стартанёт. Сразу дёргаем startTest, preflight провалится
   * и юзер увидит mic-blocked banner. Иначе в Tiny-виджете popover
   * попадает в overflow:hidden и визуально ничего не происходит, а мы
   * теряем шанс раскрыть банн.
   */
  startCallClicked(event: Event, langPopover?: Popover): void {
    if (this.micAccess === 'denied' && !this.isObserver()) {
      this.startTestWithGate();
      return;
    }
    if (this.canPromptLanguageOnStart() && langPopover) {
      langPopover.toggle(event);
      return;
    }
    this.startTestWithGate();
  }

  chooseLanguageAndStart(localeId: string, langPopover?: Popover): void {
    this.selectedLanguageLocaleId = localeId;
    if (langPopover) langPopover.hide();
    this.startTestWithGate();
  }

  /** Гейт перед реальным startTest: если агенту нужны переменные и форма
   *  ещё не открыта — открываем inline-форму в теле виджета и ждём Submit
   *  (submitVariables). Если переменные не нужны — стартуем сразу. */
  private startTestWithGate(): void {
    if (this.needsVariablesInput) {
      this.variablesFormOpen.set(true);
      return;
    }
    this.startTest();
  }

  submitVariables(): void {
    if (!this.variablesValid()) return;
    this.variablesFormOpen.set(false);
    this.startTest();
  }

  /** Имеет смысл показывать выбор языка только до старта и если их >1.
   *  Плюс уважаем `publish_options.language_dropdown` — это обычный
   *  параметр агента, а не настройка одного лишь виджета: если он явно
   *  false, выбор языка не предлагаем ни в каком режиме, стартуем сразу
   *  с дефолтом, каким бы он ни был. */
  canPromptLanguageOnStart(): boolean {
    if (this.isInSession) return false;
    if (this.languageOptions.length <= 1) return false;
    if (this.effectivePublishOptions()?.language_dropdown === false) {
      return false;
    }
    return true;
  }

  private loadAgent(): void {
    if (!this.agentId()) return;

    this.isLoadingAgent = true;
    this.cdr.markForCheck();

    this.agentsService.apiAgentsIdGet({ id: this.agentId()! }).subscribe({
      next: (agent) => {
        this.agent = agent;
        this.setVariablesSchema(agent?.variables_open_api_schema);
        this.isLoadingAgent = false;
        this.loadLanguages();
      },
      error: (error) => {
        console.error('Failed to load agent:', error);
        this.showInline('error', $localize`:@@agentTest.notify.loadFail:Failed to load agent`);
        this.isLoadingAgent = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadPublicAgent(): void {
    if (!this.agentId()) return;

    this.isLoadingAgent = true;
    this.loadAgentError = false;
    this.cdr.markForCheck();

    this.agentsService.apiAgentsIdPublicGet({ id: this.agentId()! }).subscribe({
      next: (publicAgentResult) => {
        this.publicAgent = publicAgentResult || null;
        this.fetchedAgentToken.set(publicAgentResult?.public_agent?.agent_token ?? null);
        this.loadedPublishOptions.set(publicAgentResult?.public_agent?.publish_options ?? null);
        this.setVariablesSchema(publicAgentResult?.public_agent?.variables_open_api_schema);

        if (!this.agentToken()) {
          this.showInline('error', $localize`:@@agentTest.notify.tokenFail:Failed to get agent token`);
          this.isLoadingAgent = false;
          this.loadAgentError = true;
          this.cdr.markForCheck();
          return;
        }

        this.isLoadingAgent = false;
        this.loadAgentError = false;
        this.initializeLanguageOptionsForPublicAgent();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load public agent data:', error);
        this.showInline('error', $localize`:@@agentTest.notify.publicLoadFail:Failed to load public agent data`);
        this.isLoadingAgent = false;
        this.loadAgentError = true;
        this.cdr.markForCheck();
      }
    });
  }

  private loadLanguages(): void {
    this.languagesService.apiLanguagesLanguageLocalesGet().subscribe({
      next: (languages) => {
        this.languagesData = languages;
        this.initializeLanguageOptions();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load languages:', error);
        this.initializeLanguageOptions();
        this.cdr.markForCheck();
      }
    });
  }

  private initializeLanguageOptions(): void {
    if (!this.agent?.language_locales) return;

    this.languageOptions = this.agent.language_locales.map(agentLocale => {
      const localeId = agentLocale.language_locale_id;
      if (!localeId) return null;

      let label = localeId;
      let isDefault = agentLocale.is_default || false;

      if (this.languagesData?.language_locales) {
        const ll = this.languagesData.language_locales.find(l => l.id === localeId);
        if (ll) {
          const flag = ll.locale_flag || '';
          label = `${flag}  ${ll.language_name}`;
        }
      }

      return { label, value: localeId, isDefault };
    }).filter(opt => opt !== null) as { label: string; value: string; isDefault?: boolean }[];

    const defaultLocale = this.agent.language_locales.find(ll => ll.is_default);
    this.selectedLanguageLocaleId = defaultLocale?.language_locale_id || this.languageOptions[0]?.value || null;
  }

  private initializeLanguageOptionsForPublicAgent(): void {
    if (!this.publicAgent?.public_agent?.languages) return;

    this.languageOptions = this.publicAgent.public_agent.languages.map(publishLang => {
      const localeId = publishLang.language_locale_id;
      if (!localeId) return null;

      const flag = publishLang.flag || '';
      const language = publishLang.language || localeId;
      const label = flag ? `${flag}  ${language}` : language;

      return { label, value: localeId, isDefault: publishLang.is_default || false };
    }).filter(opt => opt !== null) as { label: string; value: string; isDefault?: boolean }[];

    const defaultLang = this.publicAgent.public_agent.languages.find(l => l.is_default);
    this.selectedLanguageLocaleId = defaultLang?.language_locale_id || this.languageOptions[0]?.value || null;

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.stopAnalyticsTracking();
    this.stopAudioLevelTracking();
    this.stopMicPreview();
    this.disconnect();
    if (!this.isObserver()) {
      this.signalrService.disconnectSession();
    }
    this.unsubscribeMicPermission();
    this.sessionEnded$.complete();
  }

  async startTest(): Promise<void> {
    // Observer mode: reconnect using the stored observer token (same JWT
    // is reusable until its short TTL expires; otherwise user reloads).
    if (this.isObserver()) {
      if (this.observerRoomToken) {
        this.connectAsObserver(this.observerRoomToken);
      }
      return;
    }

    if (!this.agentId()) return;

    // Юзер явно попытался стартовать — с этого момента mic-banner можно
    // показывать. До первого клика виджет не имеет права распахиваться
    // в expanded-состояние из-за пред-existing browser-denial.
    this.micErrorRevealed = true;

    this.isLoading = true;
    this.sessionStatus = 'waiting';
    this.cdr.markForCheck();

    // Микрофон проверяем ДО dispatch'а, иначе бэкенд развернёт комнату
    // которую мы не сможем использовать, и юзер получит безликий
    // "Failed to connect" вместо понятной причины. getUserMedia сам
    // покажет браузерный prompt при необходимости.
    const micOk = await this.preflightMicrophone();
    if (!micOk) {
      this.isLoading = false;
      this.sessionStatus = 'disconnected';
      this.cdr.markForCheck();
      return;
    }

    // Ensure any previous session hub connection is closed before starting a
    // new one — fire-and-forget. If the hub is stuck in Connecting /
    // Reconnecting (typical after a failed connect), awaiting can hang
    // forever and the user's click on Start would silently do nothing.
    this.signalrService.disconnectSession().catch(() => {});

    this.sessionId = null;
    this.sessionToken = null;
    this.userStopRequested = false;

    this.dispatchAgent$().pipe(takeUntil(this.sessionEnded$)).subscribe({
      next: (dispatchResult) => {
        if (!dispatchResult.room_token) {
          this.handleStartError(new Error('Failed to get room token from dispatch result'));
          return;
        }

        console.log('[agent-test] dispatch completed, room_name:', dispatchResult.room_name);

        this.roomName = dispatchResult.room_name || null;
        this.sessionId = dispatchResult.agent_model?.session_id || null;
        this.sessionToken = dispatchResult.session_token || null;
        this.pendingRoomToken = dispatchResult.room_token;

        let connected = false;

        const doConnect = async (source: string) => {
          if (connected || !this.pendingRoomToken || this.userStopRequested) return;
          connected = true;
          console.log(`[agent-test] doConnect triggered by: ${source}, at ${new Date().toISOString()}`);

          // Agent is ready — no need to wait for SignalR anymore
          this.signalrService.disconnectSession();

          try {
            const t0 = performance.now();
            await this.connectToRoom(this.pendingRoomToken!, this.roomName || '');
            console.log(`[agent-test] connectToRoom took ${(performance.now() - t0).toFixed(0)}ms`);
            this.pendingRoomToken = null;

            // Если юзер нажал Stop пока connectToRoom был в полёте — сразу отключаемся
            if (this.userStopRequested) {
              await this.disconnect();
              return;
            }

            this.sessionStatus = 'connected';
            this.controlsAccordionValue = [];
            // Успех показывает статус-бейдж в шапке + раскрывшийся body;
            // отдельная нотификация была бы дублем.
            this.clearInlineMessage();
          } catch (error) {
            console.error(`Failed to connect to room (${source}):`, error);
            this.sessionStatus = 'disconnected';
            // Pre-check ловит большинство случаев, но юзер мог отозвать
            // право на микрофон уже в окне prompt-а или браузер мог
            // упасть на NotReadableError при захвате — здесь финальная
            // веточка, чтоб юзер не упёрся в generic "Failed to connect".
            if (this.isMicrophoneError(error)) {
              this.notifyMicError(error);
            } else {
              this.showInline('error', $localize`:@@agentTest.notify.connectFail:Failed to connect to agent`);
            }
            await this.disconnect();
          } finally {
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        };

        // STEP 1: register AgentReady callback BEFORE connecting to hub
        // This avoids a race where connect() calls registerEventHandlers() with callback=null
        console.log('[agent-test] registering AgentReady callback');
        this.signalrService.sessionHub.onAgentReady(async (message) => {
          console.log('[agent-test] AgentReady received:', message, 'at', new Date().toISOString());
          if (message.room_name !== this.roomName) {
            console.log('[agent-test] AgentReady room_name mismatch, ignoring');
            return;
          }
          await doConnect('signalr');
        });

        // STEP 2: connect to session hub using the session token from dispatch
        if (dispatchResult.session_token) {
          console.log('[agent-test] connecting to session hub');
          this.signalrService.connectSession(dispatchResult.session_token);
        }

        // STEP 3: check immediately if agent already joined (race with SignalR notification)
        if (this.agentId() && this.sessionId) {
          console.log('[agent-test] checking current session status');
          const sessionContext = dispatchResult.session_token
            ? new HttpContext().set(BEARER_TOKEN_OVERRIDE, dispatchResult.session_token)
            : undefined;
          this.sessionsService.apiSessionsSessionIdGet({
            sessionId: this.sessionId
          }, sessionContext).pipe(takeUntil(this.sessionEnded$)).subscribe({
            next: (result) => {
              console.log('[agent-test] session check result:', result?.data?.agent_joined_at ? 'agent already joined' : 'agent not joined yet');
              if (result?.data?.agent_joined_at) {
                doConnect('session-check');
              }
            },
            error: (error) => {
              console.warn('[agent-test] failed to check session status:', error);
            }
          });
        }

        if (this.connectFallbackTimer) clearTimeout(this.connectFallbackTimer);
        this.connectFallbackTimer = setTimeout(() => doConnect('timeout'), 15000);
      },
      error: (error) => this.handleStartError(error)
    });
  }

  private async handleStartError(error: any): Promise<void> {
    console.error('Failed to start test:', error);
    this.sessionStatus = 'disconnected';
    this.showInline('error', $localize`:@@agentTest.notify.connectFail:Failed to connect to agent`);
    this.isLoading = false;
    this.cdr.markForCheck();
    await this.disconnect();
  }

  copySessionId(): void {
    if (!this.sessionId) return;
    // Session-ID chip убран из UI, метод теперь вызывается только из
    // программного кода (не из template). Молча копируем без фидбека —
    // если когда-то вернём UI-кнопку, добавим обратно inline-флэш.
    navigator.clipboard.writeText(this.sessionId).catch(() => {});
  }

  async stopTest(): Promise<void> {
    // Статус-бейдж в шапке сам переключится на "Disconnected" —
    // отдельный info-toast о том же был дублем.
    await this.disconnect();
  }

  private async connectToRoom(token: string, roomName: string): Promise<void> {
    const claims = this.decodeJwtClaims(token);
    console.log('[livekit] connecting to', this.LIVEKIT_URL, {
      roomName,
      tokenRoom: claims?.video?.room ?? null,
      identity: claims?.sub ?? null,
      observer: this.isObserver(),
      canPublish: claims?.video?.canPublish ?? null,
      canSubscribe: claims?.video?.canSubscribe ?? null,
      hidden: claims?.video?.hidden ?? null,
      exp: claims?.exp ? new Date(claims.exp * 1000).toISOString() : null
    });

    // Up to 3 attempts with linear backoff. Initial connect can fail on
    // transient causes (SDK abort timeout, first-ICE-candidate refused,
    // backend race right after dispatch) — a fresh Room and a retry get
    // through them. Hard failures (bad token, room deleted, abort by us)
    // still bubble up to the catch in doConnect after attempts run out.
    const MAX_ATTEMPTS = 3;
    const BASE_DELAY_MS = 800;
    let attempt = 0;
    let lastError: unknown = null;
    while (attempt < MAX_ATTEMPTS) {
      attempt++;
      // Drop any Room left from the previous attempt — its WS is in
      // CLOSING and its handlers are still wired to `this`.
      if (this.room) {
        try { this.room.disconnect(true); } catch {}
        this.room = null;
      }
      // Bail out if the user pressed Stop while we were waiting between
      // retries (or if `disconnect()` cleared state for any reason).
      if (this.userStopRequested) {
        throw lastError ?? new Error('connect aborted');
      }
      try {
        await this.tryConnectRoomOnce(token, roomName, claims, attempt, MAX_ATTEMPTS);
        lastError = null;
        break; // success — fall through to post-connect setup (mic, analytics)
      } catch (error) {
        lastError = error;
        if (attempt >= MAX_ATTEMPTS) break;
        const delay = BASE_DELAY_MS * attempt;
        console.warn(`[livekit] retrying connect in ${delay}ms (attempt ${attempt}/${MAX_ATTEMPTS} failed)`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }

    if (lastError) {
      // All attempts exhausted — surface to caller (doConnect catch).
      throw lastError;
    }

    // Post-connect setup runs once after a successful attempt.
    this.setupAgentEventHandlers();

    if (this.isObserver()) {
      // Observer joins read-only — no mic, no analytics dispatch.
      return;
    }

    await this.enableMicrophone();
    await this.setupMicGain();
    this.startAudioLevelTracking();
    this.startAnalyticsTracking(roomName);
  }

  /**
   * Single LiveKit `Room.connect()` attempt. Creates a fresh Room with all
   * handlers and awaits `room.connect`. Throws on failure so the caller
   * can decide whether to retry or surface to the user.
   */
  private async tryConnectRoomOnce(
    token: string,
    roomName: string,
    claims: ReturnType<typeof this.decodeJwtClaims>,
    attempt: number,
    maxAttempts: number
  ): Promise<void> {
    this.room = this.isObserver()
      ? new Room()
      : new Room({
          audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          publishDefaults: {
            audioPreset: {
              maxBitrate: 128_000,
            }
          }
        });

    // SignalConnected fires once the WS signaling channel is up. LiveKit
    // cloud can redirect us to a region-specific node — that ends up on
    // `room.engine.url` after this event. Useful for diagnosing
    // signaling-vs-PC failures.
    this.room.on(RoomEvent.SignalConnected, () => {
      const engineUrl = (this.room as unknown as { engine?: { url?: string } } | null)?.engine?.url;
      console.log('[livekit] signal connected', { engineUrl: engineUrl ?? null });
      // LiveKit sends the ICE server list (STUN/TURN URLs we should use
      // for media) in its JoinResponse. Read it off the PC config so the
      // user sees exactly what addresses were advertised.
      const iceServers = this.collectIceServers();
      if (iceServers.length > 0) {
        console.log('[livekit] ICE servers advertised by LiveKit:', iceServers);
      }
    });

    this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('[livekit] connection state →', state);
      // Once we're fully connected, ask the PC for the selected ICE
      // candidate pair — that's the actual remote address we ended up
      // streaming media to/from.
      if (String(state).toLowerCase() === 'connected') {
        this.logSelectedIceCandidates();
      }
    });

    this.room.on(RoomEvent.Connected, () => {
      console.log('Connected to room:', roomName);
      this.checkForExistingAgent();
    });

    this.room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
      console.log('Participant connected:', participant.identity, 'kind:', participant.kind);
      if (participant.kind === ParticipantKind.AGENT) {
        this.agentParticipant = participant;
        this.cdr.markForCheck();
      }
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      console.log('Participant disconnected:', participant.identity, 'kind:', participant.kind);
      if (participant === this.agentParticipant) {
        this.agentParticipant = null;
        this.showInline('warn', $localize`:@@agentTest.notify.agentDisconnected:Agent disconnected`);
        this.cdr.markForCheck();
      }
      this.maybeCloseRoomIfAlone();
    });

    this.room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind === 'audio') {
        console.log('Audio track subscribed from:', participant.identity);
        const el = track.attach() as HTMLMediaElement;
        el.muted = !this.speakerOn();
        // Pre-set громкость из speakerVolume — иначе юзерский ползунок
        // до коннекта теряется, элемент стартует на дефолтном volume=1.
        el.volume = Math.max(0, Math.min(100, this.speakerVolume)) / 100;
        // LiveKit-канон: attach создаёт detached <audio>, но для
        // стабильного воспроизведения (особенно поздних SIP-треков в
        // Chrome/Safari) элемент нужно положить в DOM. Прячем через
        // display:none — только audio-роутинг нужен, не UI.
        el.style.display = 'none';
        document.body.appendChild(el);
        this.attachedAudioEls.push(el);
      }
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      if (track.kind === 'audio') {
        const detached = track.detach() as HTMLMediaElement[];
        detached.forEach(el => el.remove());
        this.attachedAudioEls = this.attachedAudioEls.filter(el => !detached.includes(el));
      }
    });

    // Если юзер зашёл с pre-toggled isMuted=true, enableMicrophone()
    // не публиковал трек — setupMicGain раннее-вышел (нечего цеплять).
    // Когда позже юзер сделает mic ON в попловере, LiveKit опубликует
    // трек, и тут мы дотягиваем gain-обвязку. Идемпотентно — повторно
    // ничего не настраивает.
    this.room.on(RoomEvent.LocalTrackPublished, (publication) => {
      if (publication.source === Track.Source.Microphone && !this.gainNode) {
        this.setupMicGain().catch(err => console.warn('setupMicGain on publish failed:', err));
      }
    });

    this.room.on(RoomEvent.Disconnected, (reason) => {
      console.log('[livekit] disconnected', { reason, reasonName: this.disconnectReasonName(reason) });
      this.sessionStatus = 'disconnected';
      this.agentParticipant = null;

      // Если разрыв НЕ инициирован юзером — глянем room.metadata: бэк
      // кладёт туда { failed, error } и при failure показываем юзеру
      // текст ошибки (с фоллбэком, если бэк не указал).
      if (!this.userStopRequested) {
        const rawMeta = this.room?.metadata;
        if (rawMeta) {
          try {
            const meta = JSON.parse(rawMeta) as RoomMetadata;
            if (meta.failed === true) {
              const errorText = meta.error?.trim()
                || $localize`:@@agentTest.notify.sessionFailedFallback:Session ended with an error`;
              this.showInline('error', errorText);
            }
          } catch {
            // Невалидный JSON — игнорируем, в логи уже упало.
          }
        }
      }

      this.cdr.markForCheck();
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      console.log('Attempting to reconnect to room');
    });

    this.room.on(RoomEvent.Reconnected, () => {
      console.log('Successfully reconnected to room');
    });

    const t0 = performance.now();
    try {
      await this.room.connect(this.LIVEKIT_URL, token);
      const engineUrl = (this.room as unknown as { engine?: { url?: string } } | null)?.engine?.url;
      console.log('[livekit] connected', {
        attempt: `${attempt}/${maxAttempts}`,
        engineUrl: engineUrl ?? null,
        elapsedMs: Math.round(performance.now() - t0)
      });
    } catch (error) {
      const err = error as { name?: string; message?: string; reason?: unknown } | null;
      console.error('[livekit] connect failed', {
        attempt: `${attempt}/${maxAttempts}`,
        url: this.LIVEKIT_URL,
        tokenRoom: claims?.video?.room ?? null,
        name: err?.name ?? null,
        message: err?.message ?? String(error),
        reason: err?.reason ?? null,
        elapsedMs: Math.round(performance.now() - t0)
      });
      throw error;
    }
  }

  /**
   * Pull RTCPeerConnections from the LiveKit Room internals. SDK has
   * shuffled this between versions (publisher/subscriber vs pcManager) —
   * we probe both shapes defensively.
   */
  private getPeerConnections(): RTCPeerConnection[] {
    const room = this.room as unknown as {
      engine?: {
        publisher?: { pc?: RTCPeerConnection };
        subscriber?: { pc?: RTCPeerConnection };
        pcManager?: {
          publisher?: { pc?: RTCPeerConnection };
          subscriber?: { pc?: RTCPeerConnection };
        };
      };
    } | null;
    const e = room?.engine;
    const out: RTCPeerConnection[] = [];
    const candidates = [
      e?.publisher?.pc,
      e?.subscriber?.pc,
      e?.pcManager?.publisher?.pc,
      e?.pcManager?.subscriber?.pc
    ];
    for (const pc of candidates) {
      if (pc && !out.includes(pc)) out.push(pc);
    }
    return out;
  }

  private collectIceServers(): RTCIceServer[] {
    const seen = new Set<string>();
    const list: RTCIceServer[] = [];
    for (const pc of this.getPeerConnections()) {
      const conf = pc.getConfiguration();
      for (const s of conf.iceServers ?? []) {
        const urls = Array.isArray(s.urls) ? s.urls : (s.urls ? [s.urls] : []);
        const key = urls.join('|') + '|' + (s.username ?? '');
        if (seen.has(key)) continue;
        seen.add(key);
        // Don't log credentials — only the URL/username pair is useful.
        list.push({ urls, username: s.username });
      }
    }
    return list;
  }

  private async logSelectedIceCandidates(): Promise<void> {
    const pcs = this.getPeerConnections();
    if (pcs.length === 0) return;
    for (const pc of pcs) {
      try {
        const stats = await pc.getStats();
        let selectedPairId: string | null = null;
        const candidates = new Map<string, any>();
        stats.forEach((r) => {
          if (r.type === 'transport' && (r as any).selectedCandidatePairId) {
            selectedPairId = (r as any).selectedCandidatePairId as string;
          }
          if (r.type === 'candidate-pair' || r.type === 'local-candidate' || r.type === 'remote-candidate') {
            candidates.set(r.id, r as any);
          }
        });
        if (!selectedPairId) {
          // Fallback: any nominated pair with state=succeeded.
          for (const [id, r] of candidates) {
            if (r?.type === 'candidate-pair' && r.state === 'succeeded' && r.nominated) {
              selectedPairId = id; break;
            }
          }
        }
        if (!selectedPairId) continue;
        const pair = candidates.get(selectedPairId) as any;
        if (!pair) continue;
        const local = candidates.get(pair.localCandidateId);
        const remote = candidates.get(pair.remoteCandidateId);
        console.log('[livekit] selected ICE pair', {
          local: local ? `${local.candidateType}://${local.address ?? local.ip}:${local.port} (${local.protocol})` : null,
          remote: remote ? `${remote.candidateType}://${remote.address ?? remote.ip}:${remote.port} (${remote.protocol})` : null,
          relay: remote?.relayProtocol ?? null
        });
      } catch (err) {
        console.warn('[livekit] failed to read ICE stats', err);
      }
    }
  }

  private decodeJwtClaims(token: string): {
    sub?: string;
    exp?: number;
    video?: { room?: string; canPublish?: boolean; canSubscribe?: boolean; hidden?: boolean };
  } | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  /** После ухода ЛЮБОГО participant'а решаем — гасить room или ждать.
   *  Считаем оставшихся remote-participants (наш local в Map'у не входит),
   *  фильтруем по kinds STANDARD | SIP | AGENT (INGRESS/EGRESS —
   *  служебные, игнорим). 0 живых — мы одни в комнате, гасим room и
   *  переводим статус. ≥1 — висим, ждём естественной смерти room через
   *  RoomEvent.Disconnected от сервера, sessionStatus не трогаем чтобы
   *  End-кнопка и input-group оставались доступны. */
  private maybeCloseRoomIfAlone(): void {
    const stayKinds = new Set<ParticipantKind>([
      ParticipantKind.STANDARD,
      ParticipantKind.SIP,
      ParticipantKind.AGENT,
    ]);
    const stillThere = [...(this.room?.remoteParticipants.values() ?? [])]
      .filter(p => stayKinds.has(p.kind));
    console.log('[agent-test] room participants left:',
      stillThere.map(p => `${p.identity}(kind:${p.kind})`));
    if (stillThere.length === 0) {
      this.sessionStatus = 'disconnected';
      this.cdr.markForCheck();
      setTimeout(() => this.disconnect(), 1000);
    }
  }

  private disconnectReasonName(reason: unknown): string {
    // Mirror DisconnectReason enum from livekit-client (proto). Plain
    // numbers come over the wire — translate to readable names.
    const names = [
      'UNKNOWN_REASON',         // 0
      'CLIENT_INITIATED',       // 1
      'DUPLICATE_IDENTITY',     // 2
      'SERVER_SHUTDOWN',        // 3
      'PARTICIPANT_REMOVED',    // 4
      'ROOM_DELETED',           // 5
      'STATE_MISMATCH',         // 6
      'JOIN_FAILURE',           // 7
      'MIGRATION',              // 8
      'SIGNAL_CLOSE',           // 9
      'ROOM_CLOSED',            // 10
      'USER_UNAVAILABLE',       // 11
      'USER_REJECTED',          // 12
      'SIP_TRUNK_FAILURE'       // 13
    ];
    if (typeof reason === 'number' && reason >= 0 && reason < names.length) {
      return names[reason];
    }
    return String(reason);
  }

  private ensureAgentTokenValid$(): Observable<void> {
    const expiresAt = this.publicAgent?.public_agent?.agent_token_expires_at;
    if (expiresAt && new Date(expiresAt).getTime() > Date.now() + 5000) {
      return of(void 0);
    }
    if (!this.agentId()) return of(void 0);

    return this.agentsService.apiAgentsIdPublicGet({ id: this.agentId()! }).pipe(
      tap((result) => {
        this.publicAgent = result || null;
        this.fetchedAgentToken.set(result?.public_agent?.agent_token ?? null);
        this.loadedPublishOptions.set(result?.public_agent?.publish_options ?? null);
        this.setVariablesSchema(result?.public_agent?.variables_open_api_schema);
        this.cdr.markForCheck();
      }),
      switchMap(() => of(void 0))
    );
  }

  private dispatchAgent$(): Observable<DispatchAgentResult> {
    if (!this.agentId()) throw new Error('Agent ID is required');

    const languageLocaleId = this.selectedLanguageLocaleId;
    const urlParameters = this.getUrlParameters();

    // Widget-режим (custom-element на чужом сайте ИЛИ /published/agents/) —
    // авторизации Keycloak нет, диспатч должен идти по внешнему bearer'у.
    // Если каллер уже прокинул token через [attr.agent-token] — доверяем ему,
    // не лезем в /public на дозакачку. Если не прокинул (embed на чужом сайте
    // без токена) — ensureAgentTokenValid$ обновит fetchedAgentToken через
    // публичный endpoint.
    const hasExternalToken = !!this.agentTokenInput();
    const ensureToken$ = this.useAgentTokenAuth() && !hasExternalToken
      ? this.ensureAgentTokenValid$()
      : of(void 0);

    return ensureToken$.pipe(
      switchMap(() => {
        const token = this.agentToken();
        const context = this.useAgentTokenAuth() && token
          ? new HttpContext().set(BEARER_TOKEN_OVERRIDE, token)
          : undefined;

        return this.agentsService.apiAgentsIdDispatchPost({
          id: this.agentId()!,
          body: {
            enable_first_message: true,
            language_locale_id: languageLocaleId,
            url_parameters: Object.keys(urlParameters).length > 0 ? urlParameters : null,
            variables: this.variablesForDispatch(),
          }
        }, context);
      }),
      tap((result) => console.log('Agent dispatched:', result))
    );
  }

  private getUrlParameters(): { [key: string]: string } {
    const params: { [key: string]: string } = {};
    const queryParams = this.route.snapshot.queryParams;
    for (const key of Object.keys(queryParams)) {
      params[key] = queryParams[key];
    }
    return params;
  }

  private setupAgentEventHandlers(): void {
    if (!this.room) return;

    this.room.on(RoomEvent.TranscriptionReceived, (segments: TranscriptionSegment[], participant?: Participant) => {
      if (!segments?.length) return;
      segments.forEach(segment => {
        const role: ChatRole = participant?.kind === ParticipantKind.AGENT ? 'Assistant' : 'User';
        const segmentId = segment.id || crypto.randomUUID();
        const streaming = !segment.final;

        const existingIndex = this.chatMessages.findIndex(msg => msg.id === segmentId);
        if (existingIndex !== -1) {
          this.chatMessages[existingIndex] = {
            ...this.chatMessages[existingIndex],
            content: segment.text,
          };
        } else {
          this.chatMessages.push({
            id: segmentId,
            role,
            kind: 'Message',
            content: segment.text,
            created_at: new Date().toISOString(),
          });
        }
        this.updateStreamingId(segmentId, streaming);
        this.chatMessages = [...this.chatMessages]; // reassign — OnPush change-detection
        this.cdr.markForCheck();
        this.scrollChatToBottom();
      });
    });

    this.room.on(RoomEvent.DataReceived, (data: Uint8Array, participant?: RemoteParticipant) => {
      if (participant && participant.kind === ParticipantKind.AGENT) {
        try {
          const message = JSON.parse(new TextDecoder().decode(data));
          if (message.type === 'tool_call_event') {
            this.handleToolCallEvent(message);
          } else {
            this.handleAgentToolMessage(message);
          }
        } catch (error) {
          console.error('Failed to parse agent message:', error);
        }
      }
    });
  }

  private handleToolCallEvent(event: any): void {
    const toolName: string = event.tool.name || event.tool.title;
    const toolCallId: string = event.tool.id;
    const createdAt = new Date(event.timestamp || Date.now()).toISOString();
    const id = `${toolCallId}-${event.event}`;

    switch (event.event) {
      case 'started':
        this.addChatMessage({
          id,
          role: 'Assistant',
          kind: 'ToolCall',
          tool_name: toolName,
          tool_call_id: toolCallId,
          tool_arguments_json: this.stringifyOrNull(event.data),
          created_at: createdAt,
        });
        break;
      case 'completed':
        this.addChatMessage({
          id,
          role: 'Assistant',
          kind: 'ToolCallResult',
          tool_name: toolName,
          tool_call_id: toolCallId,
          tool_output: this.stringifyOrNull(event.data?.response_data),
          tool_is_error: false,
          created_at: createdAt,
        });
        break;
      case 'failed':
        this.addChatMessage({
          id,
          role: 'Assistant',
          kind: 'ToolCallResult',
          tool_name: toolName,
          tool_call_id: toolCallId,
          tool_output: event.data?.error ?? null,
          tool_is_error: true,
          created_at: createdAt,
        });
        break;
    }
  }

  private stringifyOrNull(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value); }
    catch { return String(value); }
  }

  private handleAgentToolMessage(message: any): void {
    console.log('Agent tool message received:', message);
  }

  private addChatMessage(message: ChatMessageDto): void {
    this.chatMessages = [...this.chatMessages, message];
    this.cdr.markForCheck();
    this.scrollChatToBottom();
  }

  /** Помечает сообщение как стримящееся (partial transcript) или
   *  убирает метку, когда сегмент финализирован. Используется через
   *  shared chat-history-view, который рендерит спиннер по этому списку. */
  private updateStreamingId(id: string, streaming: boolean): void {
    const has = this.streamingMessageIds.includes(id);
    if (streaming && !has) {
      this.streamingMessageIds = [...this.streamingMessageIds, id];
    } else if (!streaming && has) {
      this.streamingMessageIds = this.streamingMessageIds.filter(x => x !== id);
    }
  }

  private scrollChatToBottom(): void {
    setTimeout(() => {
      if (this.chatContainer?.nativeElement) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    });
  }

  private checkForExistingAgent(): void {
    if (!this.room) return;

    for (const [, participant] of this.room.remoteParticipants) {
      if (participant.kind === ParticipantKind.AGENT) {
        this.agentParticipant = participant;
        this.cdr.markForCheck();
        break;
      }
    }
  }

  private async enableMicrophone(): Promise<void> {
    try {
      if (this.room) {
        // VU-meter preview'у нужен raw-микрофон (echoCancellation/NS/AGC=false),
        // иначе AGC сплющивает уровень. Но если preview остаётся активным
        // во время LiveKit-коннекта — Chrome применяет ОДНУ AEC-политику
        // на устройство, обычно от первого захваченного стрима с raw
        // constraint'ами, и AEC на LiveKit'овом треке фактически не
        // работает (звук из динамиков попадает в микрофон). Освобождаем
        // устройство ДО setMicrophoneEnabled — тогда LiveKit захватывает
        // его чисто с {echoCancellation: true, …}.
        this.stopMicPreview();
        // Уважаем намерение, которое юзер мог выставить до коннекта:
        // если isMuted=true — заходим в сессию сразу с приглушённым
        // микрофоном (LiveKit публикует трек, но disabled).
        const enabled = !this.isMuted;
        await this.room.localParticipant.setMicrophoneEnabled(enabled, {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        });
        this.isMuted = !this.room.localParticipant.isMicrophoneEnabled;
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Failed to enable microphone:', error);
      throw error;
    }
  }

  /**
   * Проверяем доступ к микрофону ДО dispatch'а агента. Использует
   * getUserMedia — он сам покажет prompt при необходимости. UI уже
   * заблокировал Start при micAccess='denied', так что сюда мы попадаем
   * только при 'prompt'/'granted'/'unknown'. На NotAllowedError всё
   * равно ставим micAccess='denied' (обновится через PermissionStatus
   * change, но не хочется ждать — рисуем баннер сразу).
   */
  private async preflightMicrophone(): Promise<boolean> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.micErrorText = $localize`:@@agentTest.notify.micUnsupported:Microphone API is not available in this browser. Use a recent Chrome/Firefox/Safari over HTTPS.`;
      this.cdr.markForCheck();
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      this.micAccess = 'granted';
      this.micErrorText = null;
      this.cdr.markForCheck();
      return true;
    } catch (error: any) {
      this.applyMicError(error);
      return false;
    }
  }

  /**
   * Вызывается из кнопки «Запросить доступ» в баннере. В Chrome после
   * 'denied' getUserMedia не покажет prompt — браузер должен дать юзеру
   * разрешение явно. Но это всё ещё лучшая попытка: если юзер уже
   * перерешил в адресной строке, состояние 'prompt'/'granted' подтянется,
   * и Start разблокируется.
   */
  async requestMicrophoneAccess(): Promise<void> {
    // Явное нажатие "Запросить доступ" — тоже показывает баннер / ошибку
    // на случай если revealed ещё не был проставлен (edge: юзер жмёт
    // Request access без пред-клика Start).
    this.micErrorRevealed = true;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.micErrorText = $localize`:@@agentTest.notify.micUnsupported:Microphone API is not available in this browser. Use a recent Chrome/Firefox/Safari over HTTPS.`;
      this.cdr.markForCheck();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      this.micAccess = 'granted';
      this.micErrorText = null;
      this.micRequestAttempted = false;
      this.cdr.markForCheck();
    } catch (error: any) {
      // Если это NotAllowedError — браузер не покажет prompt снова,
      // переключаем баннер на инструкцию "разблокируй вручную".
      if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
        this.micRequestAttempted = true;
      }
      this.applyMicError(error);
    }
  }

  private isMicrophoneError(error: any): boolean {
    const name = error?.name ?? '';
    return (
      name === 'NotAllowedError' ||
      name === 'NotFoundError' ||
      name === 'NotReadableError' ||
      name === 'OverconstrainedError' ||
      name === 'SecurityError'
    );
  }

  /**
   * Унифицированная обработка getUserMedia / setMicrophoneEnabled ошибок:
   * обновляем реактивное состояние (баннер) И шлём тост (если есть на
   * странице toast-host).
   */
  private applyMicError(error: any): void {
    const name = error?.name ?? '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      this.micAccess = 'denied';
      this.micErrorText = null; // у denied своя ветка баннера, отдельного текста не надо
    } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      this.micErrorText = $localize`:@@agentTest.notify.micNotFound:Microphone not found. Connect a device and try again.`;
    } else if (name === 'NotReadableError') {
      this.micErrorText = $localize`:@@agentTest.notify.micBusy:Microphone is busy with another application. Close the competing program and try again.`;
    } else {
      this.micErrorText = $localize`:@@agentTest.notify.micGeneric:Couldn't access the microphone. Check site permissions in your browser and try again.`;
    }
    // micErrorText сам рендерится в .tg-body — дублирующий тост не нужен.
    this.cdr.markForCheck();
  }

  /**
   * Кладёт inline-сообщение в .tg-body. Errors висят до явной очистки
   * или до следующего show; warn/info/success — авто-исчезновение через
   * autoHideMs.
   */
  showInline(
    type: 'error' | 'warn' | 'info' | 'success',
    text: string,
    autoHideMs = 3500,
    link?: { text: string; commands?: any[]; queryParams?: Record<string, string>; href?: string },
  ): void {
    this.clearInlineMessage();
    this.inlineMessage = { type, text, link };
    // autoHideMs = 0 → баннер висит до явной очистки (например, ссылка на
    // завершённую сессию должна оставаться пока юзер её не закроет).
    if (type !== 'error' && autoHideMs > 0) {
      this.inlineMessageTimer = setTimeout(() => this.clearInlineMessage(), autoHideMs);
    }
    this.cdr.markForCheck();
  }

  clearInlineMessage(): void {
    if (this.inlineMessageTimer) {
      clearTimeout(this.inlineMessageTimer);
      this.inlineMessageTimer = null;
    }
    this.inlineMessage = null;
    this.cdr.markForCheck();
  }

  private notifyMicError(error: any): void {
    this.applyMicError(error);
  }

  private async setupMicGain(): Promise<void> {
    if (!this.room) return;
    // Идемпотентность: если gain уже подвешен — выходим. LocalTrackPublished
    // может стрельнуть повторно (например, при mute/unmute через
    // setMicrophoneEnabled), и второй setup сломает уже работающую обвязку.
    if (this.gainNode) return;

    const micPub = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
    if (!micPub?.audioTrack) return;

    const mediaTrack = (micPub.audioTrack as LocalAudioTrack).mediaStreamTrack;
    if (!mediaTrack) return;

    this.originalMicTrack = mediaTrack;
    this.audioContext = new AudioContext();
    // Chrome требует user-gesture для активации AudioContext. К моменту
    // setupMicGain цепочка await'ов с момента клика Start уже может
    // протухнуть — без resume() gain.value меняется в JS, но реально на
    // сигнал не влияет. Явный resume фиксит.
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); }
      catch (err) { console.warn('AudioContext resume failed:', err); }
    }
    const source = this.audioContext.createMediaStreamSource(new MediaStream([mediaTrack]));
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this.micVolume / 100;

    const dest = this.audioContext.createMediaStreamDestination();
    source.connect(this.gainNode).connect(dest);

    const processedTrack = dest.stream.getAudioTracks()[0];
    await (micPub.audioTrack as LocalAudioTrack).replaceTrack(processedTrack);
  }

  /**
   * Вызывается из p-popover (onShow) кнопки микрофона. Если сессии нет —
   * поднимаем отдельный getUserMedia-стрим и AnalyserNode, в RAF считаем
   * RMS, кладём в previewLevel. UI (p-progressBar) видит живой уровень.
   * Внутри сессии preview не нужен — там уже работает audioLevel из
   * LiveKit-трека.
   */
  async onMicPopoverShow(): Promise<void> {
    this.micPopoverOpen = true;
    if (this.isConnected) return;       // в сессии уже есть audioLevel
    if (this.isMuted) return;           // юзер заглушил — не лапаем mic
    if (this.previewStream) return;     // уже запущен
    if (!navigator.mediaDevices?.getUserMedia) return;

    try {
      // RAW-микрофон: явно отрубаем AGC/NS/EC, иначе Chrome динамически
      // выравнивает громкость и VU-meter показывает плоский сигнал.
      // В сессии LiveKit ставит эти флаги обратно — там нужны для звонка.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      this.previewStream = stream;
      this.micAccess = 'granted';
      this.micErrorText = null;

      const ctx = new AudioContext();
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch { /* no-op */ }
      }
      const source = ctx.createMediaStreamSource(stream);
      // GainNode между источником и analyser — чтобы пользовательская
      // чувствительность реально влияла на уровень, который рисуем.
      // При старте сессии setupMicGain создаёт отдельную обвязку через
      // LiveKit'овский трек, но micVolume один на оба пайплайна.
      const gain = ctx.createGain();
      gain.gain.value = this.micVolume / 100;
      const analyser = ctx.createAnalyser();
      // Time-domain RMS — нам нужны сырые wave-семплы (0..255 вокруг 128),
      // а не dB-частотный спектр. fftSize=2048 даёт ~46мс окно при 44.1kHz —
      // достаточно усреднения чтобы не дёргалось, и достаточно responsive.
      analyser.fftSize = 2048;
      source.connect(gain).connect(analyser);

      this.previewAudioContext = ctx;
      this.previewAnalyser = analyser;
      this.previewGainNode = gain;
      this.previewBuffer = new Uint8Array(analyser.fftSize);
      this.cdr.markForCheck();

      const tick = () => {
        if (!this.previewAnalyser || !this.previewBuffer) return;
        this.previewAnalyser.getByteTimeDomainData(this.previewBuffer as any);
        // Time-domain: значения 0..255, центр 128. Считаем PEAK амплитуды —
        // для VU-meter'a более отзывчиво чем RMS, голос мгновенно даёт
        // всплеск, тишина = 0. RMS усредняет и смазывает реакцию.
        let peak = 0;
        for (let i = 0; i < this.previewBuffer.length; i++) {
          const v = Math.abs(this.previewBuffer[i] - 128);
          if (v > peak) peak = v;
        }
        // peak в 0..128, нормализуем в 0..100. *1.5 boost — типичный голос
        // на обычном микрофоне даёт peak ~60-80, нужно дотянуть до 100%.
        const level = Math.min(100, Math.round((peak / 128) * 100 * 1.5));
        this.previewLevel = level;
        this.cdr.markForCheck();
        this.previewRafId = requestAnimationFrame(tick);
      };
      this.previewRafId = requestAnimationFrame(tick);
    } catch (error: any) {
      this.applyMicError(error);
    }
  }

  /** Закрытие mic-popover — освобождаем preview-стрим и аудиограф. */
  onMicPopoverHide(): void {
    this.micPopoverOpen = false;
    this.stopMicPreview();
  }

  private stopMicPreview(): void {
    if (this.previewRafId != null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
    if (this.previewStream) {
      this.previewStream.getTracks().forEach(t => t.stop());
      this.previewStream = null;
    }
    if (this.previewAudioContext) {
      this.previewAudioContext.close().catch(() => {});
      this.previewAudioContext = null;
    }
    this.previewAnalyser = null;
    this.previewGainNode = null;
    this.previewBuffer = null;
    this.previewLevel = 0;
    this.cdr.markForCheck();
  }

  onMicVolumeChange(): void {
    // Preview-пайплайн (до сессии) и LiveKit-пайплайн (в сессии) держат
    // СВОИ gainNode'ы. micVolume — один источник истины: правим оба.
    if (this.previewGainNode) {
      this.previewGainNode.gain.value = this.micVolume / 100;
    }
    if (this.gainNode) {
      this.gainNode.gain.value = this.micVolume / 100;
    }
  }

  private cleanupMicGain(): void {
    if (this.originalMicTrack) {
      try { this.originalMicTrack.stop(); } catch {}
      this.originalMicTrack = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.gainNode = null;
  }

  // Audio level tracking
  private startAudioLevelTracking(): void {
    this.stopAudioLevelTracking();

    const tick = () => {
      if (this.room?.localParticipant) {
        const level = Math.round(this.room.localParticipant.audioLevel * 100);
        if (level !== this.audioLevel) {
          this.audioLevel = level;
          this.cdr.markForCheck();
        }
      }
      this.audioLevelAnimationId = requestAnimationFrame(tick);
    };

    this.audioLevelAnimationId = requestAnimationFrame(tick);
  }

  private stopAudioLevelTracking(): void {
    if (this.audioLevelAnimationId !== null) {
      cancelAnimationFrame(this.audioLevelAnimationId);
      this.audioLevelAnimationId = null;
    }
    this.audioLevel = 0;
  }

  /**
   * Переключаем намерение пользователя — мьютить ли микрофон. Работает
   * в любое время:
   * - Если нет активной сессии → просто флипаем isMuted локально.
   *   enableMicrophone() при коннекте уважает это значение.
   * - Если сессия активна → синхронно обновляем LiveKit, после ответа
   *   подтягиваем isMuted из фактического состояния трека.
   */
  async toggleMute(): Promise<void> {
    if (!this.room) {
      this.isMuted = !this.isMuted;
      // Pre-session: уважаем намерение в preview-стриме. Mute → отрубаем
      // mic полностью (browser-иконка тухнет, level=0). Unmute при
      // открытом попловере → переподнимаем getUserMedia.
      if (this.isMuted) {
        this.stopMicPreview();
      } else if (this.micPopoverOpen) {
        await this.onMicPopoverShow();
      }
      this.cdr.markForCheck();
      return;
    }

    try {
      const currentMicState = this.room.localParticipant.isMicrophoneEnabled;
      await this.room.localParticipant.setMicrophoneEnabled(!currentMicState);
      this.isMuted = !this.room.localParticipant.isMicrophoneEnabled;
      // Иконка микрофона в footer'е перечёркивается / разперечёркивается —
      // визуально понятно. Отдельный info-toast был дублем.
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Failed to toggle microphone:', error);
      this.showInline('error', $localize`:@@agentTest.notify.micToggleFail:Failed to toggle microphone`);
    }
  }

  private startAnalyticsTracking(sessionId: string): void {
    this.stopAnalyticsTracking();
    this.sessionStartTime = Date.now();
    this.sendAnalyticsEvent(sessionId);

    this.analyticsInterval = setInterval(() => {
      this.sendAnalyticsEvent(sessionId);
    }, 10000);
  }

  private stopAnalyticsTracking(): void {
    if (this.analyticsInterval) {
      clearInterval(this.analyticsInterval);
      this.analyticsInterval = null;
    }
    this.sessionStartTime = null;
  }

  private sendAnalyticsEvent(sessionId: string): void {
    if (!this.sessionStartTime) return;

    const durationSeconds = Math.floor((Date.now() - this.sessionStartTime) / 1000);

    this.analyticsService.trackEvent('agent_conversation_ongoing', {
      session_id: sessionId,
      duration_seconds: durationSeconds,
      agent_id: this.agentId()
    });
  }

  private async disconnect(): Promise<void> {
    // UI «отключено» выставляем СРАЗУ, до любого await — кнопка
    // мгновенно реагирует, даже если cleanup инициирован.
    this.userStopRequested = true;
    this.isLoading = false;
    this.sessionStatus = 'disconnected';
    this.cdr.markForCheck();

    // Гасим RxJS-подписки текущего сеанса (dispatch, session-check) и fallback-таймер
    this.sessionEnded$.next();
    if (this.connectFallbackTimer) {
      clearTimeout(this.connectFallbackTimer);
      this.connectFallbackTimer = null;
    }
    this.stopAnalyticsTracking();
    this.stopAudioLevelTracking();
    this.cleanupMicGain();

    // LiveKit — первым, без await: room.disconnect() отправляет close-фрейм
    // синхронно, агент сразу перестаёт быть с нами. Await здесь может виснуть в
    // стейте Reconnecting — поэтому файр-форгет.
    const room = this.room;
    this.room = null;
    if (room) {
      const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const localAudio = micPub?.audioTrack as LocalAudioTrack | undefined;
      if (localAudio) {
        try { localAudio.mediaStreamTrack?.stop(); } catch {}
        try { localAudio.stop(); } catch {}
      }
      // Мик индикатор в браузере — ни на что не прыжок; fire-and-forget,
      // иначе setMicrophoneEnabled может зависнуть на браузерных quirks.
      room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
      try {
        // stopTracks=true — гарантированно освобождает микрофон.
        room.disconnect(true);
      } catch (error) {
        console.error('[disconnect] room.disconnect threw', error);
      }
    }

    if (this.localAudioTrack) {
      try { this.localAudioTrack.mediaStreamTrack?.stop(); } catch {}
      try { this.localAudioTrack.stop(); } catch {}
    }
    this.localAudioTrack = null;
    this.pendingRoomToken = null;
    this.controlsAccordionValue = ['settings'];
    this.roomName = null;
    this.agentParticipant = null;
    this.isMuted = false;
    this.chatMessages = [];
    this.textMessage = '';

    // SignalR-хаб — независимая операция, её статус на LiveKit никак не влияет.
    // fire-and-forget — если хаб висит в Connecting/Reconnecting, не стопим весь cleanup.
    // В observer-режиме мы к session-хабу никогда не цеплялись — пропускаем.
    if (!this.isObserver()) {
      this.signalrService.disconnectSession().catch(() => {});
    }

    // Info-баннер со ссылкой на детали завершённой сессии. Показываем, только
    // если dispatch реально стартовал (есть sessionId) и на месте не висит
    // error-баннер (он информативнее — не затираем). Ссылка отличается по
    // режиму: в widget/public (юзер не залогинен) ведём на публичный
    // просмотр с sessionToken в query, в admin — на штатную конверсейшн-страницу.
    if (this.sessionId && !this.isObserver() && this.inlineMessage?.type !== 'error') {
      this.showSessionEndedBanner();
    }

    this.cdr.markForCheck();
  }

  /** Собирает и показывает info-баннер «Session ended — view details».
   *  Ссылка выбирается по режиму: widget/public → публичный роут с
   *  ?sessionToken=… (юзер не залогинен), admin → штатный
   *  /agents/:id/conversations/:sid. */
  private showSessionEndedBanner(): void {
    if (!this.agentId() || !this.sessionId) return;
    const agentId = this.agentId()!;
    const sid = this.sessionId;
    const text = $localize`:@@agentTest.notify.viewSession:View details`;
    // Три сценария:
    //   widget-embed (isWidgetMode=true) — виджет живёт на чужом сайте,
    //     routerLink увёл бы на origin сайта-хостера. Строим абсолютную
    //     ссылку на наш appUrl и открываем в новой вкладке.
    //   public route /published/agents/:id — юзер уже в нашем app'е,
    //     без auth. RouterLink на публичный роут работает.
    //   admin — обычный routerLink на приватную страницу сессии.
    let link;
    if (this.isWidgetMode()) {
      const qs = this.sessionToken ? `?sessionToken=${encodeURIComponent(this.sessionToken)}` : '';
      link = {
        text,
        href: `${environment.appUrl}/published/agents/${agentId}/conversations/${sid}${qs}`,
      };
    } else if (this.useAgentTokenAuth()) {
      link = {
        text,
        commands: ['/published/agents', agentId, 'conversations', sid],
        queryParams: this.sessionToken ? { sessionToken: this.sessionToken } : undefined,
      };
    } else {
      link = {
        text,
        commands: ['/agents', agentId, 'conversations', sid],
      };
    }
    this.showInline(
      'info',
      $localize`:@@agentTest.notify.sessionEnded:Session ended`,
      0, // не гасить автоматически — юзер сам закроет когда захочет перейти по ссылке
      link,
    );
  }

  // Getters
  get isConnected(): boolean {
    return this.sessionStatus === 'connected';
  }

  get isInSession(): boolean {
    return this.sessionStatus === 'connected' || this.sessionStatus === 'waiting';
  }

  /**
   * Тело карточки (зона чата) схлопнуто, если нет ничего полезного
   * показать: сессии нет (или ещё не "waiting"), mic-blocked баннера
   * нет, mic-ошибки нет, inline-сообщения тоже нет. В этом состоянии
   * header и footer прижимаются друг к другу — без бессмысленной дыры.
   */
  get bodyCollapsed(): boolean {
    // Вне widget-режима (админская страница + встроенный тестер в
    // agent-edit) карточка ВСЕГДА развёрнута: транскрипт виден по
    // умолчанию, юзер ожидает полноценную полу-fullscreen карточку,
    // а не свернутую строку.
    if (!this.isWidgetMode()) return false;
    // Widget: рисуется чат-транскрипт (если разрешён) + banner'ы.
    // Если транскрипт выключен по publish_options, сессия сама по себе
    // ничего в теле не показывает — можно схлопнуться если и banner'ов нет.
    const hasChatContent = this.isInSession && this.transcriptVisible();
    return !hasChatContent
      && !this.showMicBlockedBanner
      && !this.micErrorText
      && !this.inlineMessage
      && !this.variablesFormOpen();
  }

  get canStartTest(): boolean {
    // Микрофон denied НЕ дисейблит кнопку — пусть юзер жмёт,
    // preflightMicrophone внутри startTest() отработает и покажет
    // наш inline-баннер про доступ к микрофону. Disabled-кнопка без
    // объяснения выглядит как баг ("почему не жмётся?"), явная
    // ошибка после клика — понятнее.
    if (!(this.sessionStatus === 'none' || this.sessionStatus === 'disconnected')) return false;
    if (this.isLoading) return false;
    // Форма переменных уже открыта — юзер должен нажать Submit под ней,
    // а не Start в шапке. Дисейблим, тултип объяснит.
    if (this.variablesFormOpen()) return false;
    return true;
  }

  /** UI показывает баннер вместо Start если discovered denied И мы не
   *  observer И юзер уже пытался стартовать сессию (или явно запросил
   *  доступ). Observer заходит без микрофона — для него баннер не нужен
   *  вообще. Без revealed-гейта виджет при загрузке (когда persist-denied
   *  ещё с прошлого раза) сразу распахивался бы в expanded-стейт до
   *  всякого действия юзера. */
  get showMicBlockedBanner(): boolean {
    return this.micAccess === 'denied' && !this.isObserver() && this.micErrorRevealed;
  }

  get canStopTest(): boolean {
    return this.isInSession && !this.isLoading;
  }

  getStatusText(): string {
    switch (this.sessionStatus) {
      case 'none': return $localize`:@@agentTest.status.ready:Ready`;
      case 'waiting': return $localize`:@@agentTest.status.waiting:Waiting...`;
      case 'connected': return $localize`:@@agentTest.status.connected:Connected`;
      case 'disconnected': return $localize`:@@agentTest.status.disconnected:Disconnected`;
    }
  }

  getStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (this.sessionStatus) {
      case 'none': return 'secondary';
      case 'waiting': return 'warn';
      case 'connected': return 'success';
      case 'disconnected': return 'secondary';
    }
  }

  async sendTextMessage(): Promise<void> {
    if (!this.textMessage.trim() || !this.room) return;

    try {
      this.addChatMessage({
        id: crypto.randomUUID(),
        role: 'User',
        kind: 'Message',
        content: this.textMessage.trim(),
        created_at: new Date().toISOString(),
      });

      const textToSend = this.textMessage.trim();

      await this.room.localParticipant.sendText(textToSend, {
        topic: 'lk.chat'
      });

      this.textMessage = '';
      this.cdr.markForCheck();

    } catch (error) {
      console.error('Failed to send text message:', error);
      this.showInline('error', $localize`:@@agentTest.notify.sendFail:Failed to send message`);
    }
  }

  goBack(): void {
    this.router.navigate(['/agents', this.agentId()]);
  }

  get agentName(): string {
    const fallback = $localize`:@@agentTest.agent:Agent`;
    // В widget-режиме (embed / preview / published URL) main_label
    // задаётся владельцем агента через publish_options и служит
    // основным заголовком карточки. Вне widget-режима — внутренний
    // .title из authed load.
    if (this.isWidgetMode()) {
      return this.effectivePublishOptions()?.main_label || fallback;
    }
    return this.agent?.title || fallback;
  }
}
